import { Title, usePermissions } from "react-admin";
import { Card, CardActionArea, CardContent, Typography, Box, Stack } from "@mui/material";
import Grid from "@mui/material/Grid";
import { Link as RouterLink, Navigate } from "react-router-dom";
import dayjs from "dayjs";

import type { PermissionRecord } from "@/types/permissions";
import { formatIsoMonth } from "@/utils/date-format";
import { hasPermission } from "@/utils/permission-utils";
import {
  OFFICE_HOLIDAYS_VIEW,
  PAYROLL_PERIODS_VIEW,
  STAFF_SETTINGS_VIEW,
} from "@/containers/staff/shared/staff-payroll-permissions";

import { StaffPayrollSetupIncompleteBanner } from "@/containers/staff/shared/StaffPayrollSetupIncompleteBanner";
import {
  canAccessLeaveCalendar,
  canAccessStaffPayrollSurfaces,
  getStaffPayrollHubCards,
  shouldRedirectStaffHomeToMyAttendance,
  shouldShowMyDashboardHubCard,
} from "./staff-dashboard-helpers";

function LinkCard({ title, description, to }: { title: string; description: string; to: string }) {
  return (
    <Card variant="outlined">
      <CardActionArea component={RouterLink} to={to} sx={{ height: "100%" }}>
        <CardContent>
          <Typography variant="subtitle1" fontWeight={600}>
            {title}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {description}
          </Typography>
        </CardContent>
      </CardActionArea>
    </Card>
  );
}

export default function StaffDashboard() {
  const { permissions } = usePermissions<PermissionRecord>();
  const canManageStaff = hasPermission(permissions, "employees.view");
  const canViewMyAttendance = hasPermission(permissions, "myAttendance.view");
  const payrollFlags = {
    canViewPayrollPeriods: hasPermission(permissions, PAYROLL_PERIODS_VIEW),
    // employees.view covers setup for managers who onboard staff before dedicated payroll roles.
    canViewStaffSettings: hasPermission(permissions, STAFF_SETTINGS_VIEW) || canManageStaff,
    canViewOfficeHolidays: hasPermission(permissions, OFFICE_HOLIDAYS_VIEW) || canManageStaff,
  };
  const canAccessPayrollSurfaces = canAccessStaffPayrollSurfaces(payrollFlags);

  if (
    shouldRedirectStaffHomeToMyAttendance(
      canManageStaff,
      canViewMyAttendance,
      canAccessPayrollSurfaces
    )
  ) {
    return <Navigate to="/myAttendance" replace />;
  }

  const month = formatIsoMonth(dayjs());
  const payrollHubCards = getStaffPayrollHubCards(payrollFlags);

  return (
    <Box sx={{ p: 2 }}>
      <Title title="Staff Management" />
      <Stack spacing={2}>
        <Typography variant="h5" component="h1" fontWeight={600}>
          Staff
        </Typography>
        {canManageStaff || payrollFlags.canViewStaffSettings ? (
          <StaffPayrollSetupIncompleteBanner />
        ) : null}
        <Grid container spacing={2}>
          {canManageStaff ? (
            <>
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <LinkCard
                  title="Attendance"
                  description="Month grid for salary prep"
                  to={`/employeesAttendance?month=${month}`}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <LinkCard title="Staff roster" description="Employee profiles" to="/employees" />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <LinkCard
                  title="Leave requests"
                  description="Approve and track leave"
                  to="/employeeLeaveRequests"
                />
              </Grid>
            </>
          ) : null}
          {canAccessLeaveCalendar(canManageStaff, canViewMyAttendance) ? (
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <LinkCard
                title="Leave calendar"
                description="Month grid of team leave"
                to="/leaveCalendar"
              />
            </Grid>
          ) : null}
          {payrollHubCards.map((card) => (
            <Grid key={card.to} size={{ xs: 12, sm: 6, md: 4 }}>
              <LinkCard title={card.title} description={card.description} to={card.to} />
            </Grid>
          ))}
          {shouldShowMyDashboardHubCard(canViewMyAttendance, canManageStaff) ? (
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <LinkCard
                title="My dashboard"
                description="Your attendance and leave"
                to="/myAttendance"
              />
            </Grid>
          ) : null}
        </Grid>
      </Stack>
    </Box>
  );
}
